<template>

    <button class="btn" :type="btnType" :class="classes" :disabled="loading || disabled">
        <span>
            <span v-if="btnLabel">{{ btnLabel }}</span>
            <slot v-else></slot>
        </span>
        <div v-if="loading">
            <v-loader v-if="['primary', 'danger', 'success'].includes(btnStyle)" size="sm" type="white"></v-loader>
            <v-loader v-else :size="size" />
        </div>
    </button>

</template>

<style scoped lang="scss">

    .btn {
        @include font-semibold(14px);
        align-items: center;
        appearance: none;
        border: none;
        box-shadow: none;
        cursor: pointer;
        display: flex;
        flex-shrink: 0;
        justify-content: center;
        outline: none;
        overflow: hidden;
        position: relative;
        text-decoration: none;
        transition: all .3s;
        text-align: center;
        text-decoration: none;
        user-select: none;
        &:disabled {
            opacity: 1;
        }
        .loader {
            margin-left: 10px;
        }
    }

    .btn {
        &.btn-xs {
            border-radius: 3px;
            font-size: 10px;
            height: 30px;
            padding: 0 10px;
            &.btn-rounded {
                border-radius: 15px;
            }
        }
        &.btn-sm {
            border-radius: 3px;
            font-size: 11px;
            height: 40px;
            padding: 0 15px;
            &.btn-rounded {
                border-radius: 20px;
            }
        }
        &.btn-md {
            border-radius: 5px;
            font-size: 14px;
            height: 50px;
            padding: 0 30px;
            &.btn-rounded {
                border-radius: 25px;
            }
        }
        &.btn-lg {
            border-radius: 5px;
            font-size: 16px;
            height: 60px;
            padding: 0 30px;
            &.btn-rounded {
                border-radius: 30px;
            }
        }
    }

    .btn {
        &.btn-primary {
            background-color: $color-primary !important;
            color: #fff !important;
            &:focus {
                background-color: $color-primary !important;
                color: #fff !important;
            }
            &:hover, &:active, &.active {
                background-color: $color-primary-hover !important;
                color: #fff !important;
            }
        }
        &.btn-secondary {
            background-color: $color-secondary !important;
            color: $color-text !important;
            &:focus {
                background-color: $color-secondary !important;
                color: $color-text !important;
            }
            &:hover, &:active, &.active {
                background-color: $color-secondary-hover !important;
                color: $color-text !important;
            }
        }
        &.btn-white {
            background-color: #fff;
            border: 1px solid $color-rule;
            color: $color-text;
            &:focus {
                background-color: #fff;
                border: 1px solid $color-rule;
                color: $color-text;
            }
            &:hover, &:active {
                background-color: #fff;
                border: 1px solid $color-text;
                color: $color-text;
            }
            &.active {
                border: 1px solid $color-primary;
                color: $color-primary;
            }
        }
        &.btn-white-danger {
            background-color: #fff;
            border: 1px solid $color-rule;
            color: $color-danger !important;
            &:focus {
                background-color: #fff !important;
                border: 1px solid $color-rule !important;
                color: $color-danger !important;
            }
            &:hover, &:active, &.active {
                background-color: #fff !important;
                border: 1px solid $color-danger-hover !important;
                color: $color-danger-hover !important;
            }
        }
        &.btn-white-success {
            background-color: #fff;
            border: 1px solid $color-rule;
            color: $color-success !important;
            &:focus {
                background-color: #fff !important;
                border: 1px solid $color-rule !important;
                color: $color-success !important;
            }
            &:hover, &:active, &.active {
                background-color: #fff !important;
                border: 1px solid $color-success-hover !important;
                color: $color-success-hover !important;
            }
        }
        &.btn-success {
            background-color: $color-success !important;
            color: #fff !important;
            &:focus {
                background-color: $color-success !important;
                color: #fff !important;
            }
            &:hover, &:active, &.active {
                background-color: $color-success-hover !important;
                color: #fff !important;
            }
        }
        &.btn-danger {
            background-color: $color-danger !important;
            color: #fff !important;
            &:focus {
                background-color: $color-danger !important;
                color: #fff !important;
            }
            &:hover, &:active, &.active {
                background-color: $color-danger-hover !important;
                color: #fff !important;
            }
        }
        &.btn-google {
            background-color: #fff !important;
            border: 1px solid $color-rule;
            color: $color-text !important;
            &:focus {
                background-color: #fff !important;
                color: $color-text !important;
            }
            &:hover, &:active, &.active {
                background-color: $color-bg !important;
                color: $color-text !important;
            }
        }
        &.btn-twitter {
            background-color: $color-twitter !important;
            color: #fff !important;
            &:focus {
                background-color: $color-twitter !important;
                color: #fff !important;
            }
            &:hover, &:active, &.active {
                background-color: lighten($color-twitter, 10%) !important;
                color: #fff !important;
            }
        }
        &.btn-linkedin {
            background-color: $color-linkedin !important;
            color: #fff !important;
            &:focus {
                background-color: $color-linkedin !important;
                color: #fff !important;
            }
            &:hover, &:active, &.active {
                background-color: lighten($color-linkedin, 10%) !important;
                color: #fff !important;
            }
        }
    }

</style>

<script>

export default {

    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        btnLabel: {
            type: String,
        },
        size: {
            type: String,
            default: 'md',
        },
        btnStyle: {
            type: String,
            default: 'primary',
        },
        btnType: {
            type: String,
            default: 'button',
        },
        btnRounded: {
            type: Boolean,
            default: false,
        },
        btnBlock: {
            type: Boolean,
            default: false,
        },
        active: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        }
    },

    computed: {

        classes() {
            return {
                // Sizes
                'btn-xxs': this.size === 'xxs',
                'btn-xs': this.size === 'xs',
                'btn-sm': this.size === 'sm',
                'btn-md': this.size === 'md',
                'btn-lg': this.size === 'lg',
                // Styles
                'btn-primary': this.btnStyle === 'primary',
                'btn-secondary': this.btnStyle === 'secondary',
                'btn-danger': this.btnStyle === 'danger',
                'btn-success': this.btnStyle === 'success',
                'btn-white': this.btnStyle === 'white',
                'btn-white-danger': this.btnStyle === 'white-danger',
                'btn-white-success': this.btnStyle === 'white-success',
                'btn-google': this.btnStyle === 'google',
                'btn-linkedin': this.btnStyle === 'linkedin',
                'btn-twitter': this.btnStyle === 'twitter',
                // Utilities
                'btn-rounded': this.btnRounded,
                'btn-block': this.btnBlock,
                'active': this.active,
            };
        },

    },

}

</script>
